import React from "react";
import { modeConfig, panelTabConfig, type AssistantMode, type PanelTab } from "./modeConfig";

// Re-export type only (not the object, to satisfy react-refresh)
export type { AssistantMode, PanelTab };

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ModeSelectorProps {
  mode: AssistantMode;
  onModeChange: (mode: AssistantMode) => void;
  disabled?: boolean;
  /** Compact variant for tight spaces */
  compact?: boolean;
}

interface PanelTabSelectorProps {
  activeTab: PanelTab;
  onTabChange: (tab: PanelTab) => void;
  disabled?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab Selector (Primary - for panel header/empty state)
// ─────────────────────────────────────────────────────────────────────────────

export const ModeSelectorTabs: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  disabled = false,
  compact = false,
}) => {
  const modes: AssistantMode[] = ["quick", "workspace", "deep", "sheets"];

  return (
    <div className="inline-flex items-center rounded-lg bg-gray-800/60 p-0.5">
      {modes.map((m) => {
        const config = modeConfig[m];
        const isActive = mode === m;

        return (
          <button
            key={m}
            type="button"
            onClick={() => !disabled && onModeChange(m)}
            disabled={disabled}
            className={`
              relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
              transition-all duration-150
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${isActive
                ? `bg-${config.color}-500/20 text-${config.color}-300 shadow-sm`
                : "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
              }
            `}
          >
            <span className={isActive ? `text-${config.color}-400` : ""}>
              {config.icon}
            </span>
            <span>{compact ? config.shortLabel : config.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Compact Pill (for composer)
// ─────────────────────────────────────────────────────────────────────────────

export const ModeSelectorPill: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  disabled = false,
}) => {
  const config = modeConfig[mode];
  const modes: AssistantMode[] = ["quick", "workspace", "deep", "sheets"];
  const currentIndex = modes.indexOf(mode);
  const nextMode = modes[(currentIndex + 1) % modes.length] as AssistantMode;

  return (
    <button
      type="button"
      onClick={() => !disabled && onModeChange(nextMode)}
      disabled={disabled}
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
        border transition-all duration-150
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        bg-${config.color}-500/10 border-${config.color}-500/30 text-${config.color}-400
        hover:bg-${config.color}-500/20
      `}
      title={`Switch to ${modeConfig[nextMode].label}`}
    >
      <span className={`text-${config.color}-400`}>{config.icon}</span>
      <span>{config.shortLabel}</span>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy Card Selector (for full-page empty state)
// ─────────────────────────────────────────────────────────────────────────────

export const ModeSelectorCards: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  disabled = false,
}) => {
  const modes: AssistantMode[] = ["quick", "workspace", "deep", "sheets"];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {modes.map((m) => {
        const config = modeConfig[m];
        const isActive = mode === m;

        return (
          <button
            key={m}
            type="button"
            onClick={() => !disabled && onModeChange(m)}
            disabled={disabled}
            className={`
              relative p-3 rounded-xl text-left transition-all duration-200
              border group
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${isActive
                ? `bg-${config.color}-500/10 border-${config.color}-500/40`
                : "bg-gray-900/40 border-gray-700/40 hover:border-gray-600/50 hover:bg-gray-800/40"
              }
            `}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className={isActive ? `text-${config.color}-400` : "text-gray-400"}>
                {config.icon}
              </span>
              <span className={`text-sm font-medium ${isActive ? "text-white" : "text-gray-200"}`}>
                {config.label}
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-snug">
              {config.description}
            </p>
          </button>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Panel Tab Selector (Ask/Work - for panel header)
// ─────────────────────────────────────────────────────────────────────────────

export const PanelTabSelector: React.FC<PanelTabSelectorProps> = ({
  activeTab,
  onTabChange,
  disabled = false,
}) => {
  const tabs: PanelTab[] = ["ask", "work", "sheets"];

  return (
    <div className="inline-flex items-center rounded-lg bg-gray-800/80 backdrop-blur-sm p-1 border border-white/10">
      {tabs.map((tab) => {
        const config = panelTabConfig[tab];
        const isActive = activeTab === tab;

        return (
          <button
            key={tab}
            type="button"
            onClick={() => !disabled && onTabChange(tab)}
            disabled={disabled}
            className={`
              flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium
              transition-all duration-150
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${isActive
                ? `bg-${config.color}-500/20 text-${config.color}-300`
                : "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
              }
            `}
          >
            <span className={`flex items-center ${isActive ? `text-${config.color}-400` : ""}`}>
              {config.icon}
            </span>
            <span>{config.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quick/Deep Toggle (visible toggle switch for Ask mode)
// ─────────────────────────────────────────────────────────────────────────────

interface ResearchDepthToggleProps {
  isDeep: boolean;
  onToggle: (isDeep: boolean) => void;
  disabled?: boolean;
}

export const ResearchDepthToggle: React.FC<ResearchDepthToggleProps> = ({
  isDeep,
  onToggle,
  disabled = false,
}) => {
  const quickConfig = modeConfig.quick;
  const deepConfig = modeConfig.deep;

  return (
    <div className="flex items-center gap-1.5">
      {/* Quick option */}
      <button
        type="button"
        onClick={() => !disabled && onToggle(false)}
        disabled={disabled}
        title="Fast single-turn answers with web search"
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-150
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${!isDeep
            ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm"
            : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/50"
          }
        `}
      >
        <span className={!isDeep ? "text-amber-400" : ""}>{quickConfig.icon}</span>
        <div className="flex flex-col items-start">
          <span>Quick</span>
          <span className={`text-[10px] ${!isDeep ? "text-amber-400/70" : "text-gray-600"}`}>
            Fast answers
          </span>
        </div>
      </button>

      {/* Deep option */}
      <button
        type="button"
        onClick={() => !disabled && onToggle(true)}
        disabled={disabled}
        title="Multi-agent comprehensive research with reports and charts"
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-150
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${isDeep
            ? "bg-violet-500/20 text-violet-300 border border-violet-500/40 shadow-sm"
            : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/50"
          }
        `}
      >
        <span className={isDeep ? "text-violet-400" : ""}>{deepConfig.icon}</span>
        <div className="flex flex-col items-start">
          <span>Deep Research</span>
          <span className={`text-[10px] ${isDeep ? "text-violet-400/70" : "text-gray-600"}`}>
            Multi-agent reports
          </span>
        </div>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Default Export
// ─────────────────────────────────────────────────────────────────────────────

const ModeSelector: React.FC<ModeSelectorProps> = (props) => (
  <ModeSelectorTabs {...props} />
);

export default ModeSelector;
